import { useState, useEffect } from 'react';
import Sidebar from '../../components/sidebar/sidebar';
import Header from '../header';
import Card from '../Card';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { usePatchForm } from '../../providers/PatchFormProvider';
import SoftwareTable from '../../modules/patchcenter/softwareTable';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import api from '../services/apiService';
import Project from '../../common/Utils/project';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { usePermissions } from '../../hooks/usePermissions';

interface SupportedSoftwareVersion {
  version: string;
  osSupported: boolean;
  dateAvailable: string;
  totalAgents: number;
  softwareAgents: any[];
  vulnerabilityDetail: {
    cve_id: string;
    score: number;
    url: string;
    datePublished: string;
  }[];
}

interface Software {
  packageIdentifier: string;
  supportedSoftwareVersion: SupportedSoftwareVersion[];
}

const Availability = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState<'Explorer' | 'Availability' | 'Activity'>('Availability');
  const navigate = useNavigate();
  const location = useLocation();
  const { patchFormData, setPatchFormData } = usePatchForm();
  const [patchList, setPatchList] = useState(patchFormData.RemediationPlanPatchList || []);
  const [softwareAvailability, setSoftwareAvailability] = useState<Software[]>([]);
  const { addToast } = useToast();
  const { selectedTenantId } = useTenantSelection();
  const [isRemediating, setIsRemediating] = useState(false);
  const { canEdit } = usePermissions();
  const canRemediate = canEdit('remediationPlan');

  // Add these state variables for dual counts
  const [osUpdateCount, setOsUpdateCount] = useState(0);
  const [thirdPartyCount, setThirdPartyCount] = useState(0);

  // Add this handler
  const handleCountChange = (osCount: number, thirdPartyCount: number) => {
    setOsUpdateCount(osCount);
    setThirdPartyCount(thirdPartyCount);
  };

  const handleToggleChange = (updatedList: any[]) => {
    setPatchList(updatedList);
  };

  const handleButtonClick = async () => {
    setIsRemediating(true);

    try {
      setShowDropdown((prev) => !prev);
      console.log('🟢 Updated selection from SoftwareTable:', patchList);

      const osUpdatePatches = patchList.filter((patch) => patch.PatchType === 'OSUpdate');
      const thirdPartySoftwarePatches = patchList.filter((patch) => patch.PatchType === 'ThirdPartySoftware');

      console.log('os', osUpdatePatches);
      console.log('ThirdPartySoftware', thirdPartySoftwarePatches);

      // Bail out early if nothing is selected
      if (patchList.length === 0) {
        addToast('No patches selected', 'warning');
        return;
      }

      const baseUrl = `${Project.api}patchcenterservice/RemediationPlan/${selectedTenantId}`;

      // -------------------------
      // 🧩 Third-Party Patches API
      // -------------------------
      if (thirdPartySoftwarePatches.length > 0) {
        const thirdPartyPayload = {
          remediationPlanPatches: thirdPartySoftwarePatches.map((p) => ({
            packageIdentifier: p.PackageIdentifier,
            targetVersion: p.TargetVersion || '',
            isUpdateonly: true,
            isInstallonly: false,
          })),
        };

        console.log('📦 Sending Third-Party Remediation Payload:', thirdPartyPayload);

        await api.post(`${baseUrl}/create-remediation-plan-for-installed-agents-with-packageidentifier`, JSON.stringify(thirdPartyPayload));

        addToast(`Created remediation plan for ${thirdPartySoftwarePatches.length} third-party patch.`, 'success');
        navigate('/patchCenter');
      }

      // -------------------------
      // 🧩 OS Updates API
      // -------------------------
      if (osUpdatePatches.length > 0) {
        const osUpdatePayload = {
          windowsOSUpdateList: osUpdatePatches.map((p) => ({
            category: 'Updates',
            kbIds: [p.PackageIdentifier],
            selectUpdatesWithoutReboot: false,
            operationType: 'Install',
          })),
          macOSUpdateList: [],
          linuxOSUpdateList: [],
        };

        console.log('🪟 Sending OS Update Remediation Payload:', osUpdatePayload);

        await api.post(`${baseUrl}/create-remediation-plan-with-osupdates-for-update-available-agents`, JSON.stringify(osUpdatePayload));

        addToast(`Created remediation plan for ${osUpdatePatches.length} OS update.`, 'success');
        navigate('/patchCenter');
      }
    } catch (error) {
      console.error('❌ Failed to create remediation plan:', error);
      addToast('Failed to create remediation plan', 'error');
    } finally {
      setIsRemediating(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const patchTypeParam = searchParams.get('type') as 'OSUpdate' | 'ThirdPartySoftware' | null;
  const hasOtherParams = Array.from(searchParams.keys()).some((key) => key !== 'type');

  const [softwarePatchType, setSoftwarePatchType] = useState<'OSUpdate' | 'ThirdPartySoftware'>(
    patchTypeParam === 'OSUpdate' ? 'OSUpdate' : 'ThirdPartySoftware',
  );

  useEffect(() => {
    searchParams.set('type', softwarePatchType);
    setSearchParams(searchParams);
  }, [softwarePatchType]);

  const PatchNavigation = () => {
    navigate('/patchCenter/ActivityPatch');
  };
  const PatchNavigationExplorer = () => {
    navigate('/patchCenter');
  };
  useEffect(() => {
    return () => {
      if (window.location.pathname === '/patchCenter/deviceSelect') {
        setPatchFormData((prev) => ({
          ...prev,
          RemediationPlanPatchList: patchList,
          softwareList: softwareAvailability,
        }));
      }
    };
  }, [patchList, softwareAvailability]);

  const totalCount = osUpdateCount + thirdPartyCount;

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1" style={{ backgroundColor: 'rgb(7, 7, 7)' }}>
        <Header title="Patch Center" dropdown={2} showBackButton={hasOtherParams} />
        <Card className="flex-1 h-full" bgColor="rgb(7, 7, 7)" border={true} borderColor="#474646" borderSide="left">
          <div className="flex items-center justify-between mb-4">
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              {['Explorer', 'Availability', 'Activity'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab as 'Explorer' | 'Availability' | 'Activity');
                    if (tab === 'Activity') PatchNavigation();
                    if (tab === 'Explorer') PatchNavigationExplorer();
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    borderRight: activeTab === tab ? '5.62px solid #D12929' : '5.62px solid #474646',
                    borderLeft: activeTab === tab ? '5.62px solid #D12929' : '5.62px solid #474646',
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Selected Patches Counter + Toggle + Remediate Button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                marginRight: '20px',
              }}
            >
              {/* Counter Display - Left side */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#9CA3AF',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Selected:
                  </span>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#EF4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.15)',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                    }}
                  >
                    {totalCount}
                  </span>
                </div>

                {/* Individual counts */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    paddingLeft: 12,
                    borderLeft: '1px solid #3a3a3a',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      backgroundColor: softwarePatchType === 'OSUpdate' ? 'rgba(239, 68, 68, 0.1)' : '#2a2a2a',
                      borderRadius: '4px',
                      border: softwarePatchType === 'OSUpdate' ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid #3a3a3a',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        color: '#9CA3AF',
                        fontWeight: '500',
                      }}
                    >
                      OS Updates:
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: softwarePatchType === 'OSUpdate' ? '#EF4444' : '#666',
                      }}
                    >
                      {osUpdateCount}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      backgroundColor: softwarePatchType === 'ThirdPartySoftware' ? 'rgba(239, 68, 68, 0.1)' : '#2a2a2a',
                      borderRadius: '4px',
                      border: softwarePatchType === 'ThirdPartySoftware' ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid #3a3a3a',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        color: '#9CA3AF',
                        fontWeight: '500',
                      }}
                    >
                      Third Party Software:
                    </span>
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: softwarePatchType === 'ThirdPartySoftware' ? '#EF4444' : '#666',
                      }}
                    >
                      {thirdPartyCount}
                    </span>
                  </div>
                </div>
              </div>

              {/* Patch Type Toggle */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: softwarePatchType === 'OSUpdate' ? '#FFFFFF' : '#9CA3AF',
                    transition: 'color 0.3s ease',
                  }}
                >
                  OS Updates
                </span>

                <div
                  onClick={() => setSoftwarePatchType((prev) => (prev === 'OSUpdate' ? 'ThirdPartySoftware' : 'OSUpdate'))}
                  style={{
                    width: 48,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor: '#EF4444',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      position: 'absolute',
                      top: 2,
                      left: softwarePatchType === 'ThirdPartySoftware' ? 24 : 2,
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>

                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: softwarePatchType === 'ThirdPartySoftware' ? '#FFFFFF' : '#9CA3AF',
                    transition: 'color 0.3s ease',
                  }}
                >
                  Third Party Software
                </span>
              </div>

              {/* Remediate Button */}
              <button
                className="flex items-center space-x-2 bg-red-700 text-white px-4 py-2 rounded-lg shadow-md hover:bg-red-800 transition duration-300 disabled:bg-gray-700"
                onClick={handleButtonClick}
                disabled={patchList.length === 0 || isRemediating || !canRemediate}
              >
                {isRemediating ? (
                  <>
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Remediating...</span>
                  </>
                ) : (
                  <span>Remediate</span>
                )}
              </button>
            </div>
          </div>
          <div>
            <SoftwareTable
              softwarePatchType={softwarePatchType}
              availabilityTab={true}
              setPatchList={setPatchList}
              setSoftwareAvailability={setSoftwareAvailability}
              onToggleChange={handleToggleChange}
              onCountChange={handleCountChange}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Availability;
